import React, { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import {
  sendWhatsAppTemplate,
  sendWhatsAppText,
  setWhatsAppConsent,
} from "../../whatsappApi";

/**
 * Composer — window-aware reply box.
 *
 * Three states, because WhatsApp genuinely has three:
 *   open window   → free text
 *   closed window → the textarea is REPLACED (not merely disabled) by a
 *                   template send, because typing a message that cannot be
 *                   sent is the thing to prevent
 *   no consent / opted out → blocked entirely, with an explanation
 *
 * The no-consent state is shown BEFORE the user types, not as a refusal after
 * they press send. A patient who messaged the practice is granted consent
 * automatically on ingest, so reaching this state means nobody has recorded it —
 * and the fix is offered inline rather than sending the user to the patient
 * record and back.
 *
 * The server enforces all of this too (a stale tab would otherwise post into a
 * window that closed two minutes ago); this is the affordance, not the rule.
 */

const REENGAGE = "practosync_reengage";

export default function Composer({
  practiceNumber,
  conversation,
  templates,
  onSent,
  onLinkPatient,
  onConsentChanged,
  showToast,
}) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [templateName, setTemplateName] = useState(REENGAGE);

  const approved = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates],
  );

  if (!conversation) return null;

  const blocked = conversation.optedOut;
  const unlinked = !conversation.patientId;

  // `consent` is undefined when the caller did not load it and null when the
  // thread is unlinked, so only a linked thread can report consent missing.
  const consent = conversation.consent;
  const withdrawn = !!consent && consent.decided && !consent.granted;
  const consentMissing = !!consent && !consent.decided && !consent.granted;

  const fail = (err, fallback) =>
    showToast?.("danger", err?.response?.data?.error || fallback);

  const recordConsent = async () => {
    setRecordingConsent(true);
    try {
      await setWhatsAppConsent(conversation.patientId, true, "practice");
      showToast?.("success", "WhatsApp consent recorded for this patient.");
      onConsentChanged?.();
    } catch (err) {
      fail(err, "The consent could not be recorded.");
    } finally {
      setRecordingConsent(false);
    }
  };

  const sendText = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { message } = await sendWhatsAppText(
        practiceNumber,
        conversation._id,
        body,
      );
      setBody("");
      onSent?.(message);
    } catch (err) {
      fail(err, "The message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async () => {
    const template = approved.find((t) => t.name === templateName);
    if (!template) return;

    setSending(true);
    try {
      // Both standard re-engagement templates take patient name then practice
      // name; anything else is configured in PracticeSettings.
      const params = [
        conversation.patientName?.split(" ")[0] ||
          conversation.profileName ||
          "there",
        conversation.practiceName || "your practice",
      ].slice(0, template.paramCount);

      const { message } = await sendWhatsAppTemplate(practiceNumber, {
        conversationId: conversation._id,
        templateName: template.name,
        languageCode: template.language,
        params,
      });
      onSent?.(message);
    } catch (err) {
      fail(err, "The template could not be sent.");
    } finally {
      setSending(false);
    }
  };

  // A staff withdrawal on the patient record reads the same to the user as an
  // inbound STOP, so it shares this surface rather than inventing a second one.
  if (blocked || withdrawn) {
    return (
      <div className="wa-composer">
        <Alert variant="warning" className="mb-0">
          This patient has opted out of WhatsApp. You cannot send messages until
          they opt in again on their patient record.
        </Alert>
      </div>
    );
  }

  // Nothing recorded either way: the send would be refused by the server, so the
  // consent is asked for here rather than letting the user discover that.
  if (consentMissing) {
    return (
      <div className="wa-composer">
        <Alert variant="warning" className="mb-0 d-flex align-items-center gap-2">
          <span className="flex-fill">
            No WhatsApp consent is recorded for this patient, so messages cannot
            be sent yet.
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Record that the patient has agreed to WhatsApp messages about
                their appointments and account.
              </Tooltip>
            }
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={recordConsent}
              disabled={recordingConsent}
            >
              {recordingConsent ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Record consent"
              )}
            </Button>
          </OverlayTrigger>
        </Alert>
      </div>
    );
  }

  return (
    <div className="wa-composer">
      {unlinked && (
        <Alert variant="warning" className="py-2 px-3 d-flex align-items-center gap-2">
          <span className="flex-fill">
            This number is not linked to a patient.
          </span>
          <Button variant="outline-primary" size="sm" onClick={onLinkPatient}>
            Link to patient
          </Button>
        </Alert>
      )}

      {conversation.windowOpen ? (
        <div className="d-flex align-items-end gap-2">
          <Form.Control
            as="textarea"
            rows={2}
            value={body}
            placeholder="Type a reply…"
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
            disabled={sending}
          />
          <OverlayTrigger placement="top" overlay={<Tooltip>Send</Tooltip>}>
            <Button
              variant="primary"
              onClick={sendText}
              disabled={sending || !body.trim()}
            >
              {sending ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <i className="fa-solid fa-paper-plane" />
              )}
            </Button>
          </OverlayTrigger>
        </div>
      ) : (
        <>
          <Alert variant="info" className="py-2 px-3">
            You can only send a free message within 24 hours of the patient's
            last message. Send an approved template to reopen the conversation.
          </Alert>

          {approved.length > 1 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {approved.map((t) => (
                <Button
                  key={`${t.name}-${t.language}`}
                  size="sm"
                  style={{ minWidth: 120 }}
                  variant={
                    templateName === t.name ? "secondary" : "outline-secondary"
                  }
                  onClick={() => setTemplateName(t.name)}
                >
                  {t.name.replace("practosync_", "").replace(/_/g, " ")}
                </Button>
              ))}
            </div>
          )}

          <Button
            variant="primary"
            onClick={sendTemplate}
            disabled={sending || unlinked || !approved.length}
          >
            {sending ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-2" />
                Send template
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
